import { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { internalAuthService, InternalUser } from '@/services/internalAuthService';
import { supabase } from '@/integrations/supabase/client';
import { useUserActivity } from './useUserActivity';

interface InternalAuthContextType {
  user: InternalUser | null;
  profile: any | null;
  login: (username: string, password: string, pin: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
  initialCheckComplete: boolean;
  register: (userData: any) => Promise<{ success: boolean; error?: string; user?: InternalUser }>;
  generateSeedPhrase: () => Promise<string[]>;
  resetPassword: (username: string, seedPhraseArray: string[], newPassword: string) => Promise<{ success: boolean; error?: string }>;
  resetPin: (username: string, seedPhraseArray: string[], newPin: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updatePin: (oldPin: string, newPin: string) => Promise<{ success: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const InternalAuthContext = createContext<InternalAuthContextType | undefined>(undefined);

export function InternalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<InternalUser | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);
  const isLoggingOutRef = useRef(false);

  // Track user activity for online status
  useUserActivity(user?.id, 'internal');

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // Skip initialization if user is logging out
      if (isLoggingOutRef.current) {
        console.log('🔐 INTERNAL: Skipping initialization during logout');
        return;
      }
      
      console.log('🔐 INTERNAL: Initializing authentication...');
      
      try {
        // Initialize users (sync between localStorage and Supabase) in background first
        // This runs as a pure background operation that doesn't interfere with session
        internalAuthService.initializeUsers().catch(console.error);
        
        // Quick check for existing session to reduce loading time
        const storedUser = internalAuthService.getCurrentUser();
        
        // Also check for founding agent session in localStorage
        let foundingAgentUser = null;
        const foundingAgentSession = localStorage.getItem('foundingAgentSession');
        if (foundingAgentSession) {
          try {
            const session = JSON.parse(foundingAgentSession);
            if (session.applicationId && session.username) {
              foundingAgentUser = {
                id: session.applicationId,
                username: session.username,
                role: 'agent' as const,
                age_group: 'adult' as const,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                is_active: true,
                account_verified: true
              };
            }
          } catch (e) {
            console.error('Error parsing founding agent session:', e);
          }
        }
        
        const effectiveUser = storedUser || foundingAgentUser;
        
        if (mounted && effectiveUser) {
          console.log('🔐 INTERNAL: Found stored user, setting user state:', effectiveUser.username);
          setUser(effectiveUser);
          setInitialCheckComplete(true);
          setLoading(false);
          
          // Async refresh user data in background (only for regular internal users)
          if (storedUser) {
            refreshUserData(storedUser.id).catch(console.error);
            loadProfileData(storedUser.id).catch(console.error);
          }
        } else {
          console.log('🔐 INTERNAL: No stored user found');
          if (mounted) {
            setInitialCheckComplete(true);
            setLoading(false);
          }
        }
        
      } catch (error) {
        console.error('InternalAuth: Error during initialization:', error);
        if (mounted) {
          setInitialCheckComplete(true);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const loadProfileData = async (userId: string) => {
    try {
      console.log('InternalAuth: Loading profile data for user ID:', userId);
      const { data, error } = await supabase
        .from('internal_user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('InternalAuth: Error loading profile:', error);
        return;
      }

      if (data) {
        console.log('InternalAuth: Profile data loaded:', data);
        setProfile(data);
      } else {
        console.log('InternalAuth: No profile data found for user, will create one');
        // If no profile exists, we'll create one during the next profile update
        setProfile(null);
      }
    } catch (error) {
      console.error('InternalAuth: Error loading profile data:', error);
    }
  };

  const refreshUserData = async (userId: string) => {
    try {
      console.log('InternalAuth: Refreshing user data for ID:', userId);
      const freshUser = await internalAuthService.getUserById(userId);
      if (freshUser) {
        // Only update state if data actually changed to prevent cascading re-renders
        setUser(prevUser => {
          if (prevUser && JSON.stringify(prevUser) === JSON.stringify(freshUser)) {
            console.log('InternalAuth: User data unchanged, skipping update');
            return prevUser; // Return same reference to prevent re-renders
          }
          console.log('InternalAuth: Refreshed user data:', freshUser.username);
          // Update session storage with fresh data
          localStorage.setItem('internal_user_session', JSON.stringify({
            user: freshUser,
            timestamp: Date.now()
          }));
          return freshUser;
        });
      } else {
        console.log('InternalAuth: No user found in database for ID:', userId);
        // User not found in database, clear session
        logout();
      }
    } catch (error) {
      console.error('InternalAuth: Error refreshing user data:', error);
    }
  };

  const refreshUser = async () => {
    if (user) {
      console.log('InternalAuth: Manual refresh requested for user:', user.username);
      await refreshUserData(user.id);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      console.log('InternalAuth: Manual profile refresh requested for user:', user.username);
      await loadProfileData(user.id);
    }
  };

  const login = async (username: string, password: string, pin: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('InternalAuth: Login attempt for username:', username);
      const result = await internalAuthService.login(username, password, pin);
      if (result.success && result.user) {
        console.log('InternalAuth: Login successful, setting user:', result.user.username);
        
        // CRITICAL: Sign out of Supabase to prevent dual-session conflicts
        // This ensures internal user's role is not overridden by Supabase profile
        try {
          console.log('InternalAuth: Signing out Supabase to prevent session conflict');
          await supabase.auth.signOut();
        } catch (signOutError) {
          console.error('InternalAuth: Failed to sign out Supabase (non-fatal):', signOutError);
        }
        
        // Set user state immediately for faster navigation
        setUser(result.user);
        
        // Load profile in background without blocking
        ensureProfileExists(result.user).catch(console.error);
        
        console.log('InternalAuth: Login complete, profile loading in background');
      } else {
        console.log('InternalAuth: Login failed:', result.error);
      }
      return result;
    } catch (error) {
      console.error('InternalAuth: Login error:', error);
      return { success: false, error: 'Login failed' };
    }
  };

  const register = async (userData: any): Promise<{ success: boolean; error?: string; user?: InternalUser }> => {
    try {
      console.log('InternalAuth: Registration attempt for username:', userData.username);
      const result = await internalAuthService.register({
        username: userData.username,
        password: userData.password,
        pin: userData.pin,
        seedPhrase: userData.seedPhrase,
        role: userData.role,
        ageGroup: userData.ageGroup,
        referralCode: userData.referralCode
      });
      
      if (result.success && result.user) {
        console.log('InternalAuth: Registration successful, creating profile for user:', result.user.username);
        
        // CRITICAL: Sign out of Supabase to prevent dual-session conflicts
        try {
          console.log('InternalAuth: Signing out Supabase to prevent session conflict');
          await supabase.auth.signOut();
        } catch (signOutError) {
          console.error('InternalAuth: Failed to sign out Supabase (non-fatal):', signOutError);
        }
        
        // Set user state immediately for faster navigation
        setUser(result.user);
        
        // Create initial profile in background without blocking
        createInitialProfile(result.user).catch(console.error);
        
        console.log('InternalAuth: Registration complete, profile creating in background');
        return { success: true, user: result.user };
      } else {
        console.log('InternalAuth: Registration failed:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('InternalAuth: Registration error:', error);
      return { success: false, error: 'Registration failed' };
    }
  };

  const ensureProfileExists = async (user: InternalUser) => {
    try {
      console.log('InternalAuth: Ensuring profile exists for user:', user.username);
      
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('internal_user_profiles')
        .select('user_id')
        .eq('user_id', user.id)
        .single();

      if (!existingProfile) {
        console.log('InternalAuth: Profile does not exist, creating one for:', user.username);
        await createInitialProfile(user);
      } else {
        console.log('InternalAuth: Profile already exists for:', user.username);
        // Reload the profile data
        await loadProfileData(user.id);
      }
    } catch (error) {
      console.error('InternalAuth: Error ensuring profile exists:', error);
    }
  };

  const createInitialProfile = async (user: InternalUser) => {
    try {
      console.log('InternalAuth: Creating initial profile for user:', user.username);
      
      const { error } = await supabase
        .from('internal_user_profiles')
        .insert({
          user_id: user.id,
          username: user.username,
          role: user.role,
          show_profile_public: true,
          show_contact_public: false,
        });

      if (error) {
        console.error('InternalAuth: Error creating initial profile:', error);
      } else {
        console.log('InternalAuth: Initial profile created successfully for:', user.username);
        await loadProfileData(user.id);
      }
    } catch (error) {
      console.error('InternalAuth: Error creating initial profile:', error);
    }
  };

  const generateSeedPhrase = async (): Promise<string[]> => {
    const words = ['apple', 'banana', 'cherry', 'date', 'elderberry', 'fig', 'grape', 'honeydew', 'kiwi', 'lemon', 'mango', 'orange'];
    return words.sort(() => 0.5 - Math.random()).slice(0, 12);
  };

  const resetPassword = async (username: string, seedPhraseArray: string[], newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const seedPhrase = seedPhraseArray.join(' ');
      return await internalAuthService.resetPassword(username, newPassword, newPassword, seedPhrase);
    } catch (error) {
      return { success: false, error: 'Password reset failed' };
    }
  };

  const resetPin = async (username: string, seedPhraseArray: string[], newPin: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const seedPhrase = seedPhraseArray.join(' ');
      return await internalAuthService.resetPin(username, newPin, newPin, seedPhrase);
    } catch (error) {
      return { success: false, error: 'PIN reset failed' };
    }
  };

  const updatePassword = async (oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!user) return { success: false, error: 'No user logged in' };
      return await internalAuthService.updatePassword(user.id, oldPassword, newPassword);
    } catch (error) {
      return { success: false, error: 'Password update failed' };
    }
  };

  const updatePin = async (oldPin: string, newPin: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!user) return { success: false, error: 'No user logged in' };
      return await internalAuthService.updatePin(user.id, oldPin, newPin);
    } catch (error) {
      return { success: false, error: 'PIN update failed' };
    }
  };

  const logout = () => {
    console.log('InternalAuth: Logging out user:', user?.username);
    
    // Set flag immediately using ref to prevent re-initialization
    isLoggingOutRef.current = true;
    
    // Clear all auth-related localStorage immediately
    localStorage.removeItem('internal_user_session');
    localStorage.removeItem('foundingAgentSession');
    
    // Call service logout
    internalAuthService.logout();
    
    // Immediately reset state
    setUser(null);
    setProfile(null);
    
    // Reset the logout flag after a longer delay to allow navigation to complete
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 500);
  };

  // Add debugging info to help track state changes
  useEffect(() => {
    console.log('InternalAuth: User state changed to:', user?.username || 'null');
  }, [user]);

  const value = {
    user,
    profile,
    login,
    logout,
    loading,
    initialCheckComplete,
    register,
    generateSeedPhrase,
    resetPassword,
    resetPin,
    updatePassword,
    updatePin,
    refreshUser,
    refreshProfile
  };

  return (
    <InternalAuthContext.Provider value={value}>
      {children}
    </InternalAuthContext.Provider>
  );
}

export function useInternalAuth() {
  const context = useContext(InternalAuthContext);
  if (context === undefined) {
    throw new Error('useInternalAuth must be used within an InternalAuthProvider');
  }
  return context;
}
